import {Object3D, property, WonderlandEngine} from '@wonderlandengine/api';
import {DefaultGameRules} from './default-game-rules.js';
import {Localization} from '../../utils/Localization.js';
import {StatNames} from '../../enums/StatNames.js';
import {Statistics} from '../statistics.js';
import {Analytics, AnalyticsEvent} from '../../classes/Analytics.js';
import {specialMode} from './game-rules.js';
import {LeaderboardManager} from '../leaderboard-manager.js';
import {GameModeType} from '../../enums/GameModeType.js';
import {timeSinceEpoch} from '../../utils/Utils.js';
import {Puzzle, PuzzleConstants} from '../puzzle.js';
import {Tags} from '../tags.js';
import {FruitAutoExplodePower} from '../special-powers/fruit-auto-explode-power.js';
import {BoardColor, BoardObject} from '../board-object.js';
import {RNG} from '../../utils/rng.js';

export class CupcakeGameRules extends DefaultGameRules {
    static TypeName = 'cupcake-game-rules';
    static InheritProperties = true;

    static onRegister(engine: WonderlandEngine) {
        engine.registerComponent(FruitAutoExplodePower);
    }

    @property.float(0.5)
    startingDropSpeedIncrease = 0.5;

    @property.int(5000)
    startingCupcakeWorth = 5000;

    private _numCupcakesEaten = 0;
    private _originalDropSpeedIncreaseFactor = 0;
    private _previousFruitPlaced: boolean = false;
    private _noLevelUp: boolean = false;

    start() {
        super.start();
    }

    override startNewGame(): void {
        super.startNewGame();
        this._originalDropSpeedIncreaseFactor = this.dropSpeedIncreaseFactor;

        if (this.startingDropSpeedIncrease > 0) {
            Puzzle.instance.dropSpeedFactor = this.startingDropSpeedIncrease;
        }

        Puzzle.instance.score.showCupcakeModeHud(true);

        this._numCupcakesEaten = 0;

        Puzzle.instance.score.addCupcakesCount(0);
    }

    override levelUp(showText: boolean): boolean {
        if (this._noLevelUp) {
            // if we don't want to level up, we need to set the drop speed factor increase
            // to 0 so it doesn't increase.
            this._noLevelUp = false;
            this.dropSpeedIncreaseFactor = 0;
        }

        const result = super.levelUp(showText);

        // Reset the drop speed increase factor to the original value,
        // so it it can be increase next time around
        this.dropSpeedIncreaseFactor = this._originalDropSpeedIncreaseFactor;

        return result;
    }

    incrementNumberCupcakes() {
        this._numCupcakesEaten++;
        Puzzle.instance.score.addCupcakesCount(this._numCupcakesEaten);
    }

    getNumCupcakes() {
        return this._numCupcakesEaten;
    }

    override fruitPreSpawn(obj: Object3D): Object3D {
        obj = super.fruitPreSpawn(obj);

        if (Tags.hasTag(obj, 'Fruit')) {
            const power = obj.getComponent(FruitAutoExplodePower);
            if (!power) {
                obj.addComponent(FruitAutoExplodePower);
            }
        }
        return obj;
    }

    override showFinalResults() {
        const combo = this.puzzleController.score.combo;

        this.puzzleController.score.showFinalResults(
            Localization.instance.get('BestChainFormat'),
            combo,
            Localization.instance.get('Cupcake'),
            StatNames.RecentHighScoreCupcake
        );
    }

    override getHighScore() {
        const highScore = Statistics.instance.getLong(StatNames.HighScoreCupcake);
        return highScore;
    }

    override getWeeklyHighScore() {
        return Statistics.instance.getLong(StatNames.RecentHighScoreCupcake);
    }

    override saveHighScore(score: number) {
        super.saveHighScore(score);
        Statistics.instance.recordHighest(StatNames.HighScoreCupcake, score);

        if (Statistics.instance.recordHighest(StatNames.RecentHighScoreCupcake, score)) {
            Statistics.instance.record(
                StatNames.RecentHighScoreCupcakeTimestamp,
                timeSinceEpoch()
            );
        }

        LeaderboardManager.instance
            .saveScoreToPlatformLeaderboard(score, GameModeType.Cupcake)
            .catch((e) => {
                console.error('Error saving score to platform leaderboard:', e);
            });
    }

    override saveCumulativeScore(score: number) {
        Statistics.instance.recordAdditive(StatNames.CumulativeScoreCupcake, score);
        Statistics.instance.recordAdditive(StatNames.CumulativeScoreAny, score);

        let roundedScore: number;

        if (score > 10000000) {
            roundedScore = score - (score % 1000000);
        } else if (score > 1000000) {
            roundedScore = score - (score % 1000000);
        } else if (score > 100000) {
            roundedScore = score - (score % 100000);
        } else if (score > 10000) {
            roundedScore = score - (score % 10000);
        } else {
            roundedScore = score - (score % 1000);
        }

        //Logger.Log("score logged " + score + " = " + roundedScore);
        Analytics.LogEventWithValue(AnalyticsEvent.ScoreCupcake, roundedScore);
    }

    override incrementGamesPlayed() {
        Statistics.instance.recordAdditive(StatNames.CupcakeGamesPlayed, 1);
        Statistics.instance.recordAdditive(StatNames.TotalGamesPlayed, 1);

        // if (this.getSpecialMode(specialMode.FreezeDrop) == true)
        // {
        // 	Statistics.instance.recordAdditive(StatNames.FreezeDropGamesPlayed, 1);
        // }
    }

    override beatGame() {
        Statistics.instance.recordAdditive(StatNames.BeatCupcakeMode, 1);
    }

    /**
     * Prefer a color for the blobs.
     * For Cupcake modes we check the top row of colors and pick a random one from there.
     * But, since it's 1 turn ahead this is not 100% accurate. Blobs can be removed.
     */
    override preferColor(color: BoardColor): BoardColor {
        // To make cupcake mode easier, we influence the spawning of fruits
        // based on the color of the blobs.
        // We first need to create an array of the top row of colors
        const topRowColors: BoardColor[] = [];
        for (let column = 0; column < PuzzleConstants.columnCount; column++) {
            for (let row = PuzzleConstants.rowCount - 1; row >= 0; row--) {
                const obj = Puzzle.instance.getObject(row, column);
                if (obj) {
                    // We only care about the first object in the column,
                    // if it's a fruit we'll skip the column
                    if (!obj.isFruit) {
                        const bc = obj.object.getComponent(BoardObject)!.color;
                        topRowColors.push(bc);
                    }
                    break;
                }
            }
        }

        // if we have a previous color, remove 1 of those from the list
        if (this._previousSpawnedFruit) {
            const index = topRowColors.indexOf(this._previousSpawnedFruit);
            if (index !== -1) {
                topRowColors.splice(index, 1);
            }
        }

        const newColor = RNG.getItem<BoardColor>(topRowColors) ?? color;
        this._previousSpawnedFruit = newColor;
        // We then pick a random color from the row and use that as a color for the blobs.
        return newColor;
    }

    fruitPopped() {
        this._previousFruitPlaced = false;
    }
    fruitPlaced() {
        if (this._previousFruitPlaced) {
            // there was a fruit placed, but not immidately popped. This means that
            // we're not going to level up at the end of the turn.
            this._noLevelUp = true;
        }
        this._previousFruitPlaced = true;
    }

    private _previousSpawnedFruit: BoardColor | null = null;
}
